import { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { styled } from '@mui/material/styles';
import { submitContext } from '../../../contexts/submit.context';

// ── SubmitError ──────────────────────────────────────────────────────

type SubmitErrorProps = {
	className?: string;
};

/**
 * Full-width error banner rendered inside the submit form area.
 *
 * Reads `error` from `submitContext` and renders a dismissable message that
 * replaces the normal Attach + content + actions layout while an error is
 * active.  Clicking anywhere on the banner clears the error.
 *
 * The component should only be rendered when `submitContext.error` is truthy;
 * the caller is responsible for the conditional guard.
 */
export const SubmitError = styled(
	({ className }: SubmitErrorProps) => {
		const { error, setError } = useContext(submitContext);

		const handleClick = useCallback(() => {
			setError(undefined);
		}, [setError]);

		return (
			<strong
				className={classNames('submit-error', className)}
				onClick={handleClick}
			>
				{error}
			</strong>
		);
	},
	{ label: 'SubmitError', target: 'SubmitError-root' },
)({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	width: '100%',
	padding: '0 1em',
	cursor: 'pointer',
	color: 'var(--chat__footer__form__error_color, var(--chat__footer_color))',
	fontSize: '1.3em',
	textAlign: 'center',
	userSelect: 'none',
});
